// How รายงานการโอน is addressed by URL — the two routes and the query params they carry.
//
// The summary keeps its filters in the query string rather than in component state, which is
// what makes the customer's "เลือกวันที่ไว้แล้ว … ย้อนกลับมาจะไปทำสาขาอื่นต่อได้เลย" work: the
// drill-down carries that same query string, so both the ย้อนกลับ control and the browser's
// own back button land on the summary exactly as it was left.

/** The branch-day summary (cross-branch readers) / route-level report (branch readers). */
export const SALE_DEPOSIT_REPORT_PATH = "/report/deposit";

/**
 * คำขอแก้ไขยอดโอน — the queue of requests to reopen a deposit the report has already frozen.
 * A sibling path rather than a child of the report's own, so it can never be mistaken for a
 * branch code by the drill-down pattern below.
 */
export const SALE_DEPOSIT_EDIT_REQUEST_PATH = "/report/deposit-edit-requests";

/** Route pattern for the drill-down; its two params are read with `useParams`. */
export const SALE_DEPOSIT_BRANCH_DAY_PATH = "/report/deposit/:branchCode/:businessDate";

/**
 * Query-param names, shared so the screen that writes one and the screen that reads it back
 * cannot disagree. `status` is read by BOTH screens on purpose: a summary row's figures are
 * aggregated under it, so the drill-down has to apply the same filter or it would list routes
 * the row never counted.
 */
export const DEPOSIT_REPORT_PARAM = {
  dateFrom: "date_from",
  dateTo: "date_to",
  branchCode: "branch_code",
  routeCode: "route_code",
  status: "status",
  closingStatus: "closing_status",
  page: "page",
} as const;

/** `URLSearchParams.toString()` output — no leading "?". */
const withQuery = (path: string, search: string) =>
  search ? `${path}?${search}` : path;

/** The drill-down URL for one branch-day, carrying the summary's filters as the return ticket. */
export const branchDayDetailUrl = (
  branchCode: string,
  businessDate: string,
  summarySearch: string,
): string =>
  withQuery(
    `${SALE_DEPOSIT_REPORT_PATH}/${encodeURIComponent(branchCode)}/${encodeURIComponent(businessDate)}`,
    summarySearch,
  );

/**
 * Back to the summary with the filters the drill-down was opened from.
 *
 * `route_code` is dropped on the way: it narrows one branch-day and the summary has no route
 * filter at all, so carrying it back would leave it in the URL to be handed to the NEXT
 * branch-day opened — a route that belongs to another branch, silently emptying its table.
 */
export const saleDepositSummaryUrl = (summarySearch: string): string => {
  const summaryParams = new URLSearchParams(summarySearch);
  summaryParams.delete(DEPOSIT_REPORT_PARAM.routeCode);
  return withQuery(SALE_DEPOSIT_REPORT_PATH, summaryParams.toString());
};
